import { ChangeDetectionStrategy, Component, computed, inject, type OnInit } from '@angular/core';

import { ResizableDirective, type ResizeEvent, ResizeHandleDirective } from 'angular-resizable-element';

import { TooltipModule } from 'ngx-bootstrap/tooltip';

import type { Source } from '@sendpulse/plyr';

import { EHorizontalPosition } from '@common/enums';

import type { WebLegacyWidgetEntity } from '@web/types';

import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import { BLANK_VIDEO } from '@web/widgets/image/constants/image.consts';

import type { CssVars, LegacyVideoDefault, VideoEntity } from '@sites/entity-core';
import type { VideoDefaultForm, VideoForm, VideoZeroForm } from '@sites/entity-signal-form';

import { SpPlayerComponent } from '@libs/components/sp-player/src/sp-player.component';

import { environment } from '@web-env/environment';

import { getVideoPlayButtonCssVars } from '@sites/entity-core/src/widget/video/styles/video.css-vars';

@Component({
    selector: 'web-video-view',
    templateUrl: './video-view.component.html',
    styleUrls: ['./video-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpPlayerComponent, TooltipModule, ResizableDirective, ResizeHandleDirective],
})
export class VideoViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<VideoEntity['data']>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);

    public form!: VideoForm;

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<VideoForm>(this.widget().id, this.destroyRef);
    }

    public readonly isZero = computed<boolean>(() => this.form?.value().type === 'zero');

    public get defaultForm(): VideoDefaultForm {
        return this.form as VideoDefaultForm;
    }

    public get zeroForm(): VideoZeroForm {
        return this.form as VideoZeroForm;
    }

    private readonly data = computed<VideoEntity['data']>(() => this.form.value().data);

    public readonly isEmpty = computed<boolean>(() => !this.form.videoUrl().value() && !this.form.videoFile().value());

    // --- CSS custom property values (bound on the wrapper in the template) ---
    public readonly previewSize = computed<string>(() =>
        this.form.isShorts().value() && !this.form.customPreviewFile().value()
            ? 'cover'
            : this.form.customPreviewSize().value()
    );
    public readonly aspectRatio = computed<string>(() => (this.form.isShorts().value() ? '9 / 16' : '16 / 9'));
    public readonly videoWidth = computed<string | null>(() => {
        const width = this.form.value().styles?.width;
        return width != null ? (width as string) : null;
    });
    public readonly videoDisplay = computed<string | null>(() => {
        const display = this.form.value().styles?.display;
        return display != null ? (display as string) : null;
    });
    public readonly videoMargin = computed<string>(() => {
        switch ((this.data() as LegacyVideoDefault['data']).horizontalPosition) {
            case EHorizontalPosition.L:
                return '0 auto 0 0';
            case EHorizontalPosition.R:
                return '0 0 0 auto';
            default:
                return '0 auto';
        }
    });
    public readonly videoMaxWidth = computed<string | null>(() => {
        const maxWidth = (this.data() as LegacyVideoDefault['data']).maxWidth;
        return maxWidth != null ? `${maxWidth}px` : null;
    });

    /** Play-button size + colour CSS variables, bound as a map on the root. */
    public readonly playButtonCssVars = computed<CssVars>(() => getVideoPlayButtonCssVars(this.data()));

    public readonly videoSource = computed<Source[]>(() => {
        const videoType = this.form.videoType().value();

        if (videoType !== 'fileManager') {
            return [
                {
                    src: this.form.videoUrl().value(),
                    provider: videoType as any,
                },
            ];
        }

        return [
            {
                src: this.videoFileUrl() as string,
                provider: 'html5',
            },
        ];
    });

    public readonly videoFileUrl = computed<string | boolean>(() => {
        const file = this.form.videoFile().value();
        if (!file) {
            return false;
        }
        return `${environment.fileManagerCdnUrl}${file.url}`;
    });
    public readonly blankVideoImg = computed(() => BLANK_VIDEO);

    public onResizeStart(_event: ResizeEvent, _target: HTMLElement): void {}

    public onResizing(event: ResizeEvent, target: HTMLElement): void {
        if (typeof event.edges.bottom !== 'number') return;
        if (event.rectangle.width <= 50) return;

        target.style.maxWidth = event.rectangle.width + 'px';
    }

    public onResizeEnd(event: ResizeEvent, target: HTMLElement): void {
        if (!this.isZero()) {
            this.defaultForm.setMaxWidth(event.rectangle.width);
        }
        target.style.maxWidth = '';
    }
}
